import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import TipsList from '../components/TipsList';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { downloadFile } from '../utils/download';
import { Download, ArrowRight, RotateCcw, Settings, Check, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const IMAGE_FORMATS = ['jpg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'avif', 'ico', 'pdf'];

// The page is reached via several distinct query-param combos from the
// homepage/footer (?from=jpg&to=png, ?to=webp, ?mode=resize, ...), but
// previously served one identical <title>/<meta> regardless of which —
// meaning Google saw several different URLs all claiming the same title,
// and none of them was actually optimised for the specific conversion a
// visitor searched for. Building the SEO tags from the same params the
// page already reads for its own functionality fixes both problems at
// once: distinct, keyword-matched titles per URL, no more near-duplicate
// meta across query variants.
function getSeoMeta(searchParams) {
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const mode = searchParams.get('mode');
  const upper = f => f.toUpperCase();

  if (mode === 'resize') {
    return {
      h1: 'Resize Image',
      title: 'Resize Image Online Free — Change Dimensions | ILoveConvert',
      desc: 'Resize any image online for free — set exact width and height, or scale proportionally. No signup, no watermark.',
      path: '/image-convert?mode=resize',
    };
  }
  if (from && to) {
    return {
      h1: `${upper(from)} to ${upper(to)} Converter`,
      title: `${upper(from)} to ${upper(to)} Converter Online Free | ILoveConvert`,
      desc: `Convert ${upper(from)} to ${upper(to)} online for free — fast, no signup, no watermark. Batch conversion supported.`,
      path: `/image-convert?from=${from}&to=${to}`,
    };
  }
  if (to) {
    return {
      h1: `Image to ${upper(to)} Converter`,
      title: `Image to ${upper(to)} Converter Online Free | ILoveConvert`,
      desc: `Convert JPG, PNG, and other image formats to ${upper(to)} online for free — fast, no signup, no watermark.`,
      path: `/image-convert?to=${to}`,
    };
  }
  return {
    h1: 'Image Converter',
    title: 'Image Converter Online Free — JPG PNG WebP | ILoveConvert',
    desc: 'Convert images between JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, ICO and PDF. Free online image converter. No signup.',
    path: '/image-convert',
  };
}

export default function ImageConvert() {
  const [searchParams] = useSearchParams();
  const seo = getSeoMeta(searchParams);
  const [files, setFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState(searchParams.get('to') || 'webp');
  const [quality, setQuality] = useState(90);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  // If the selected output format turns out to match the file that was just
  // uploaded (e.g. the ?to= URL param, or the 'webp' default, happens to
  // equal the input's own format), auto-switch to a real alternative —
  // otherwise the "Convert to .X" button below would offer a same-format
  // "conversion" with no matching option even highlighted in the grid above.
  useEffect(() => {
    if (!files.length) return;
    const inputExt = files[0].name.split('.').pop().toLowerCase();
    const inputNormalized = inputExt === 'jpeg' ? 'jpg' : inputExt;
    if (outputFormat === inputNormalized) {
      setOutputFormat(IMAGE_FORMATS.find(f => f !== inputNormalized));
    }
    // eslint-disable-next-line
  }, [files]);

  const handleConvert = async () => {
    if (!files.length) return toast.error('Please select an image');
    trackEvent('conversion_started', { tool_name: 'Image Convert' });
    setStatus('uploading'); setProgress(20); setError(''); setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('outputFormat', outputFormat);
    formData.append('quality', quality);
    if (width) formData.append('width', width);
    if (height) formData.append('height', height);

    try {
      const sessionId = localStorage.getItem('sessionId') ||
  Math.random().toString(36).substring(2) + Date.now().toString(36);

      const res = await axios.post('/api/convert/single', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'x-session-id': sessionId }
      });

      const { conversionId } = res.data;
      setStatus('processing'); setProgress(50);

      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 60) { clearInterval(poll); setStatus('error'); setError('Timed out'); return; }
        try {
          const sr = await axios.get(`/api/convert/status/${conversionId}`);
          if (sr.data.status === 'done') {
            clearInterval(poll); setStatus('done'); setProgress(100); setResult(sr.data);
            toast.success('Image converted!');
          } else if (sr.data.status === 'error') {
            clearInterval(poll); setStatus('error'); setError(sr.data.error || 'Failed');
          } else {
            setProgress(p => Math.min(p + 5, 90));
          }
        } catch { clearInterval(poll); setStatus('error'); setError('Server error'); }
      }, 1500);
    } catch (err) {
      setStatus('error'); setError((err.response && err.response.data && err.response.data.error) || 'Upload failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); setProgress(0); };

  return (
    <div>

      <Helmet>
  <title>{seo.title}</title>
  <meta name="description" content={seo.desc} />
  <meta name="keywords" content="image converter, jpg to png, png to jpg, convert image online, webp converter, free image converter, image format converter" />
  <link rel="canonical" href={`https://iloveconvert.in${seo.path}`} />
  <meta property="og:title" content={seo.title} />
  <meta property="og:description" content={seo.desc} />
  <meta property="og:url" content={`https://iloveconvert.in${seo.path}`} />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={seo.title} />
  <meta name="twitter:description" content={seo.desc} />
</Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}></div>
        <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', marginBottom: 8 }}>{seo.h1}</h1>
        <p>Convert between JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, ICO and more</p>
        <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
    Convert images online instantly between JPG, PNG, WebP, GIF, BMP, TIFF and more.
    This tool allows you to optimize, resize, and change image formats easily without
    installing any software. Upload your image, choose format and settings, and download instantly.
  </p>
</div>
      </section>
      <div style={{ maxWidth: 900, margin: '10px auto 0', padding: '0 20px' }}>
  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
    Our image converter is designed to provide fast, reliable, and high-quality file conversions for everyday use. Whether you are working with personal photos, website assets, or professional design files, this tool helps you quickly convert images into the format you need without compromising quality. It supports all major image formats including JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, and ICO, making it suitable for a wide range of use cases.
  </p>

  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)', marginTop: 10 }}>
    In addition to format conversion, you can also optimize your images for better performance by reducing file size, adjusting resolution, or preparing images for web and mobile applications. This is especially useful for developers, designers, bloggers, and anyone looking to improve website speed and user experience. Everything runs on our servers, so there is no need to download or install any software.
  </p>

  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)', marginTop: 10 }}>
    We prioritize your privacy and security by ensuring that all uploaded files are processed securely and automatically deleted within 2 hours. You can convert your images with confidence knowing your data isn't kept around. With a simple interface and powerful features, our image converter makes file conversion easy, efficient, and accessible for everyone.
  </p>
</div>

      <div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={{ 'image/*': ['.jpg','.jpeg','.png','.webp','.gif','.bmp','.tiff','.tif','.avif','.ico','.svg'] }} />

            {files.length > 0 && (
              <div className="convert-options">
                <div className="option-row">
                  <span className="option-label">Convert to:</span>
                  <div className="format-grid">
                    {/* Converting a file to its own format isn't a real conversion —
                        offering it as an option is just confusing. jpg/jpeg are the
                        same format under two names, so both get excluded together. */}
                    {IMAGE_FORMATS.filter(fmt => {
                      const inputExt = files[0].name.split('.').pop().toLowerCase();
                      const inputNormalized = inputExt === 'jpeg' ? 'jpg' : inputExt;
                      return fmt !== inputNormalized;
                    }).map(fmt => (
                      <button key={fmt} className={`format-btn ${outputFormat === fmt ? 'active' : ''}`} onClick={() => setOutputFormat(fmt)}>
                        .{fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem' }}
                    onClick={() => setShowOptions(!showOptions)}>
                    <Settings size={14} /> Advanced options {showOptions ? '▲' : '▼'}
                  </button>
                </div>

                {showOptions && (
                  <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                    <div>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>Quality: {quality}%</label>
                      <input type="range" min="10" max="100" value={quality} onChange={e => setQuality(+e.target.value)} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>Max Width (px)</label>
                      <input type="number" placeholder="e.g. 1920" value={width} onChange={e => setWidth(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '2px solid var(--border)', borderRadius: 8, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>Max Height (px)</label>
                      <input type="number" placeholder="e.g. 1080" value={height} onChange={e => setHeight(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '2px solid var(--border)', borderRadius: 8, fontFamily: 'inherit' }} />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 20 }}>
                  <button className="btn btn-primary btn-lg" onClick={handleConvert} style={{ width: '100%' }}>
                    <ArrowRight size={18} /> Convert to .{outputFormat.toUpperCase()}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {(status === 'uploading' || status === 'processing') && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Converting image...</p>
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>Image Converted!</h3>
              <p>Your image is ready to download.</p>
              <button onClick={() => downloadFile(result.downloadUrl, result.downloadUrl.split('/').pop(), 'Image Convert')} className="download-btn">
                <Download size={18} /> Download .{outputFormat.toUpperCase()}
              </button>
              <ShareButtons toolLabel="Image Convert" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Convert Another</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}

       
      </div>
      

      
<section className="hero">
      {/* ── SEO Content Block — paste into ConvertPage.js and Home.js ── */}
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>


  {/* ── How it works ──────────────────────────────────────── */}
 <h1>How it works <span>Instantly</span></h1>
 <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16, textAlign: 'left' }}>Convert any file in 4 simple steps</h2>
  <TipsList tips={[
      { label: 'Upload your file', tip: 'Drag and drop or click to browse. Supports files up to 50 MB. Works from desktop, tablet, or mobile.' },
      { label: 'Choose your output format', tip: 'Select from the available output formats. Options are filtered to what makes sense for your input file type.' },
      { label: 'Click Convert', tip: 'Processing happens on our secure servers. Most conversions complete in under 10 seconds.' },
      { label: 'Download your file', tip: 'Your converted file downloads directly. Both input and output files are deleted automatically after 2 hours.' },
    ]} />
  </div>
</section>
 <section className="section" style={{ background: 'linear-gradient(135deg, #1D3557, #005CFC 55%, #193A6D)' }}>
  <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
      <h2 className="section-title" style={{ color: 'white' }}>All formats we support</h2>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { title: 'PDF documents', tags: ['PDF → DOCX','PDF → XLSX','PDF → JPG','DOCX → PDF','XLSX → PDF','JPG → PDF'], desc: 'Convert PDFs to editable formats or create PDFs from Office documents and images.' },
      { title: 'Images',        tags: ['JPG → PNG','PNG → JPG','Any → WebP','Any → GIF','Any → BMP','Any → AVIF','Any → ICO'], desc: 'Convert between all major image formats with quality and resize controls.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{card.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {card.tags.map(t => (
            <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, border: '1px solid var(--border)', background: '#f8fafc', color: 'var(--text-muted)' }}>{t}</span>
          ))}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section" style={{ background: '#f8fafc' }}>
       
<div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
    <h2 className="section-title">What makes this converter different</h2>
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { color: '#3B82F6', title: 'No software needed',  desc: 'Use it right from your browser. Nothing to install. Works on Windows, Mac, Linux, iOS and Android.' },
      { color: '#22C55E', title: 'Privacy first',        desc: 'Uploaded over HTTPS. Files deleted automatically within 2 hours. We never share your files.' },
      { color: '#F59E0B', title: 'High quality output',  desc: 'Powered by LibreOffice, Sharp, and pdf-lib — the same engines used by enterprise tools.' },
      { color: '#A855F7', title: 'Generous limits',     desc: 'Up to 50 MB per file. No watermarks. No premium plans. Free means free.' },
      { color: '#005CFC', title: 'Fast processing',      desc: 'Most conversions finish in under 10 seconds. No queue, no waiting.' },
      { color: '#0EA5E9', title: 'Mobile friendly',      desc: 'Full converter on phones and tablets. Upload from camera roll, download directly.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderLeft: `3px solid ${card.color}`, borderRadius: '0 12px 12px 0', padding: '14px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{card.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section hero" style={{ background: 'white' }}>
       
 <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
  {/* ── Pro tips ──────────────────────────────────────────── */}

    <h1>Tips for best <span> results</span></h1>
   <TipsList tips={[
      // PDF to Word and Compress PDF tips hidden while those tools are off.
      // { label: 'PDF to Word',        tip: 'PDFs with selectable text convert far better than scanned documents. Scanned PDFs need OCR for editable output.' },
      { label: 'Reduce image size',  tip: 'Convert JPG or PNG to WebP to reduce file size by 25–35% with minimal visible quality loss for most images — ideal for websites and email.' },
      { label: 'Images to PDF',      tip: 'Use JPG to PDF to combine multiple images into one document — great for sharing photo albums or scanned paperwork.' },
      // { label: 'Compress PDF',       tip: 'Use Compress PDF before emailing large files. Typically reduces size by 30–60% with no visible quality change.' },
      { label: 'Resize first',       tip: 'Resize very large photos to the size you actually need before converting — the result is smaller and loads faster.' },
    ]} />
</div>
</section>
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif', alignItems: 'center',
}}>
  {/* ── FAQ ───────────────────────────────────────────────── */}
     <h2 className="section-title">Frequently asked questions</h2>
   <FaqAccordion faqs={[
  {
    q: 'Is this image converter free to use?',
    a: 'Yes, our image converter is completely free. You can convert images between formats like JPG, PNG, WebP, GIF, and more without any hidden charges or watermarks.'
  },
  {
    q: 'Are my uploaded images secure?',
    a: 'Yes, your images are processed securely over HTTPS. Files are stored temporarily for conversion and automatically deleted within 2 hours to protect your privacy.'
  },
  {
    q: 'Which image formats are supported?',
    a: 'You can convert between popular formats such as JPG, PNG, WebP, GIF, BMP, TIFF, AVIF, and ICO. The tool supports most common image types used for web and design.'
  },
  {
    q: 'Can I reduce image size while converting?',
    a: 'Yes, you can adjust quality settings and resize dimensions to reduce file size. Converting to WebP is especially effective for reducing size without losing quality.'
  },
  {
    q: 'Will image quality be affected?',
    a: 'Our tool is optimized to maintain high quality. However, compression settings may slightly reduce quality depending on the selected format and quality level.'
  },
  {
    q: 'Can I convert images on mobile devices?',
    a: 'Yes, the image converter works on all devices including smartphones and tablets. You can upload images from your gallery and download the converted files instantly.'
  }
]} />
</div>
    </div>
  );
}
